import axios from 'axios'
import { BASE_URL, ENDPOINTS } from './endpoints'
import { clearAuth, getAuthToken } from './auth'

const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    Authorization: 'Basic ' + btoa('amplify:amplify')
  }
})

apiClient.interceptors.request.use((config) => {
  const token = getAuthToken()
  config.headers = config.headers || {}
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  } else if (!config.headers.Authorization) {
    config.headers.Authorization = 'Basic ' + btoa('amplify:amplify')
  }
  return config
})

const AUTH_ERROR_PATTERNS = [
  'session expired',
  'invalid token',
  'token expired',
  'unauthorized',
  'authorised',
  'forbidden',
  'login again'
]

const isAuthErrorMessage = (value) => {
  if (!value || typeof value !== 'string') return false
  const normalized = value.toLowerCase()
  return AUTH_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern))
}

const redirectToLogin = () => {
  if (typeof window === 'undefined') return
  const currentPath = `${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}`
  if ((window.location.pathname || '') === '/login') return
  clearAuth()
  const redirect = currentPath && currentPath !== '/' ? `?redirect=${encodeURIComponent(currentPath)}` : ''
  window.location.assign(`/login${redirect}`)
}

apiClient.interceptors.response.use(
  (response) => {
    const data = response?.data || {}
    const statusCode = response?.status
    const authLikeStatus = data?.status === 401 || data?.status === 403
    const authLikeMessage =
      isAuthErrorMessage(data?.msg) ||
      isAuthErrorMessage(data?.message) ||
      isAuthErrorMessage(data?.error)

    if (statusCode === 401 || statusCode === 403 || authLikeStatus || authLikeMessage) {
      redirectToLogin()
    }

    return response
  },
  (error) => {
    const statusCode = error?.response?.status
    const data = error?.response?.data || {}
    const authLikeStatus = data?.status === 401 || data?.status === 403
    const authLikeMessage =
      isAuthErrorMessage(data?.msg) ||
      isAuthErrorMessage(data?.message) ||
      isAuthErrorMessage(data?.error)

    if (statusCode === 401 || statusCode === 403 || authLikeStatus || authLikeMessage) {
      redirectToLogin()
    }

    return Promise.reject(error)
  }
)

const api = {  

  getVendors(formData){
     return apiClient.post(ENDPOINTS.GET_VENDORS, formData)
  }, 

  getVendorDetail(formData){
     return apiClient.post(ENDPOINTS.VENDOR_DETAIL, formData)
  },

  getVendorPackages(formData){
     return apiClient.post(ENDPOINTS.VENDOR_PACKAGES, formData)
  },

  getVendorProducts(formData){
     return apiClient.post(ENDPOINTS.VENDOR_PRODUCTS, formData)
  },

  getVenuesInvite(formData){
     return apiClient.post(ENDPOINTS.GET_VENUES_INVITE, formData)
  },

  getVenueDetailInvite(formData){
     return apiClient.post(ENDPOINTS.GET_VENUE_DETAIL_INVITE, formData)
  },

  login(formData){
     return apiClient.post(ENDPOINTS.LOGIN, formData)
  },

  loginVendor(formData){
     return apiClient.post(ENDPOINTS.LOGIN_VENDOR, formData)
  },

  register(formData){
     return apiClient.post(ENDPOINTS.REGISTER, formData)
  },

  registerVendor(formData){
     return apiClient.post(ENDPOINTS.REGISTER_VENDOR, formData)
  },

  getProfile(formData){
     return apiClient.post(ENDPOINTS.GET_PROFILE, formData)
  },

  getProfileVendor(formData){
     return apiClient.post(ENDPOINTS.GET_PROFILE_VENDOR, formData)
  },

  listBusinessVendor(formData){
     return apiClient.post(ENDPOINTS.LIST_BUSINESS_VENDOR, formData)
  },

  addBusinessVendor(formData){
     return apiClient.post(ENDPOINTS.ADD_BUSINESS_VENDOR, formData)
  },

  editBusinessVendor(formData){
     return apiClient.post(ENDPOINTS.EDIT_BUSINESS_VENDOR, formData)
  },

  updateBusinessVendor(formData){
     return apiClient.post(ENDPOINTS.UPDATE_BUSINESS_VENDOR, formData)
  },

  updateBusinessContactVendor(formData){
     return apiClient.post(ENDPOINTS.UPDATE_BUSINESS_CONTACT_VENDOR, formData)
  },

  updateBusinessDocumentsVendor(formData){
     return apiClient.post(ENDPOINTS.UPDATE_BUSINESS_DOCUMENTS_VENDOR, formData)
  },

  updateBusinessBankVendor(formData){
     return apiClient.post(ENDPOINTS.UPDATE_BUSINESS_BANK_VENDOR, formData)
  },

  deleteBusinessVendor(formData){
     return apiClient.post(ENDPOINTS.DELETE_BUSINESS_VENDOR, formData)
  },

  addVendorBusinessVenue(formData){
     return apiClient.post(ENDPOINTS.ADD_VENDOR_BUSINESS_VENUE, formData)
  },

  getVenueHighlights(formData){
     return apiClient.post(ENDPOINTS.GET_VENUE_HIGHLIGHTS, formData)
  },

  getVenueTypes(formData){
     return apiClient.post(ENDPOINTS.GET_VENUE_TYPES, formData)
  },

  getVenueEventsHosted(formData){
     return apiClient.post(ENDPOINTS.GET_VENUE_EVENTS_HOSTED, formData)
  },

  getVenueAmenities(formData){
     return apiClient.post(ENDPOINTS.GET_VENUE_AMENITIES, formData)
  },

  getVenueInhouseServices(formData){
     return apiClient.post(ENDPOINTS.GET_VENUE_INHOUSE_SERVICES, formData)
  },

  getServiceVendorCategories(formData){
     return apiClient.post(ENDPOINTS.GET_SERVICE_VENDOR_CATEGORIES, formData)
  },

  listVenuesBusinessVendor(formData){
     return apiClient.post(ENDPOINTS.LIST_VENUES_BUSINESS_VENDOR, formData)
  },

  updateVendorVenueDetails(formData){
     return apiClient.post(ENDPOINTS.UPDATE_VENDOR_VENUE_DETAILS, formData)
  },

  publishVendorVenueDetails(formData){
     return apiClient.post(ENDPOINTS.PUBLISH_VENDOR_VENUE_DETAILS, formData)
  },

  addPackageVenue(formData){
     return apiClient.post(ENDPOINTS.ADD_PACKAGE_VENUE, formData)
  },

  updatePackageVenue(formData){
     return apiClient.post(ENDPOINTS.UPDATE_PACKAGE_VENUE, formData)
  },

  editPackageVenue(formData){
     return apiClient.post(ENDPOINTS.EDIT_PACKAGE_VENUE, formData)
  },

  getPackagesVenue(formData){
     return apiClient.post(ENDPOINTS.GET_PACKAGES_VENUE, formData)
  },

  deletePackageVenue(formData){
     return apiClient.post(ENDPOINTS.DELETE_PACKAGE_VENUE, formData)
  },

  addPoliciesVenue(formData){
     return apiClient.post(ENDPOINTS.ADD_POLICIES_VENUE, formData)
  },

  editPoliciesVenue(formData){
     return apiClient.post(ENDPOINTS.EDIT_POLICIES_VENUE, formData)
  },

  addVenueVendorPartner(formData){
     return apiClient.post(ENDPOINTS.ADD_VENUE_VENDOR_PARTNER, formData)
  },

  updateVenueVendorPartner(formData){
     return apiClient.post(ENDPOINTS.UPDATE_VENUE_VENDOR_PARTNER, formData)
  },

  listVenueVendorPartners(formData){
     return apiClient.post(ENDPOINTS.LIST_VENUE_VENDOR_PARTNERS, formData)
  },

  deleteVenueVendorPartner(formData){
     return apiClient.post(ENDPOINTS.DELETE_VENUE_VENDOR_PARTNER, formData)
  },

  getVenueGalleryCategories(formData){
     return apiClient.post(ENDPOINTS.GET_VENUE_GALLERY_CATEGORIES, formData)
  },

  getVenueGallery(formData){
     return apiClient.post(ENDPOINTS.GET_VENUE_GALLERY, formData)
  },

  addVenueGallery(formData){
     return apiClient.post(ENDPOINTS.ADD_VENUE_GALLERY, formData)
  },

  deleteVenueGallery(formData){
     return apiClient.post(ENDPOINTS.DELETE_VENUE_GALLERY, formData)
  },

  updateVenueCoverImage(formData){
     return apiClient.post(ENDPOINTS.UPDATE_VENUE_COVER_IMAGE, formData)
  },

  getVenueMetaDetails(formData){
     return apiClient.post(ENDPOINTS.GET_VENUE_META_DETAILS, formData)
  },

  updateVenueMetaDetails(formData){
     return apiClient.post(ENDPOINTS.UPDATE_VENUE_META_DETAILS, formData)
  },

  getVenueAvailability(formData){
     return apiClient.post(ENDPOINTS.GET_VENUE_AVAILABILITY, formData)
  },

  updateAvailabilityVenue(formData){
     return apiClient.post(ENDPOINTS.UPDATE_AVAILABILITY_VENUE, formData)
  },

  getSpaceGallery(formData){
     return apiClient.post(ENDPOINTS.GET_SPACE_GALLERY, formData)
  },

  addSpaceGallery(formData){
     return apiClient.post(ENDPOINTS.ADD_SPACE_GALLERY, formData)
  },

  getVenueSpaceTypes(formData){
     return apiClient.post(ENDPOINTS.GET_VENUE_SPACE_TYPES, formData)
  },

  getVenueSpaceSeatConfig(formData){
     return apiClient.post(ENDPOINTS.GET_VENUE_SPACE_SEAT_CONFIG, formData)
  },

  addVenueSpace(formData){
     return apiClient.post(ENDPOINTS.ADD_VENUE_SPACE, formData)
  },

  updateVenueSpace(formData){
     return apiClient.post(ENDPOINTS.UPDATE_VENUE_SPACE, formData)
  },

  deleteVenueSpace(formData){
     return apiClient.post(ENDPOINTS.DELETE_VENUE_SPACE, formData)
  },

  listVenueSpaces(formData){
     return apiClient.post(ENDPOINTS.LIST_VENUE_SPACES, formData)
  },

  addVenue(formData){
     return apiClient.post(ENDPOINTS.ADD_VENUE, formData)
  },

  updateVenue(formData){
     return apiClient.post(ENDPOINTS.UPDATE_VENUE, formData)
  },

  deleteVenue(formData){
     return apiClient.post(ENDPOINTS.DELETE_VENUE, formData)
  },

  getSubcategories(formData){
     return apiClient.post(ENDPOINTS.GET_SUBCATEGORIES, formData)
  },

  listVendorPackages(formData){
     return apiClient.post(ENDPOINTS.LIST_VENDOR_PACKAGES, formData)
  },

  addVendorPackage(formData){
     return apiClient.post(ENDPOINTS.ADD_VENDOR_PACKAGE, formData)
  },

  deleteVendorPackage(formData){
     return apiClient.post(ENDPOINTS.DELETE_VENDOR_PACKAGE, formData)
  },

  updateVendorPackage(formData){
     return apiClient.post(ENDPOINTS.UPDATE_VENDOR_PACKAGE, formData)
  }

}

export default api
